import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, NBTextarea, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, CheckCircle2, Wrench } from "lucide-react";

const CATEGORIES = ["HVAC", "Plumbing", "Electrical", "Lawn", "Cleaning", "Vehicle", "Appliance", "general"];

export default function Maintenance() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "general", frequency_days: 30, last_done: "", next_due: "" });

  const load = async () => {
    const { data } = await api.get("/api/maintenance");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/maintenance", form);
      toast.success("Saved!");
      setOpen(false);
      setForm({ title: "", description: "", category: "general", frequency_days: 30, last_done: "", next_due: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const complete = async (id) => {
    await api.patch(`/api/maintenance/${id}/complete`);
    toast.success("Marked complete!");
    load();
  };

  const remove = async (id) => { await api.delete(`/api/maintenance/${id}`); load(); };

  const isDue = (item) => {
    if (!item.next_due) return false;
    return new Date(item.next_due) <= new Date();
  };

  return (
    <div data-testid="maintenance-page">
      <PageHeader
        title="House Maintenance"
        subtitle="Keep recurring chores on track."
        color="#E9B5A0"
        action={<NBButton testId="add-maintenance-btn" color="#E9B5A0" onClick={() => setOpen(true)}><Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Task</NBButton>}
      />

      {items.length === 0 ? (
        <EmptyState title="No maintenance tasks" subtitle="Track recurring home chores like HVAC, lawn care, etc." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <NBCard key={it.id} color={isDue(it) ? "#E9B5A0" : "white"} data-testid={`maintenance-${it.id}`}>
              <div className="flex justify-between items-start">
                <Wrench strokeWidth={2.5} />
                <button onClick={() => remove(it.id)} className="nb-btn bg-white p-1.5" data-testid={`delete-maintenance-${it.id}`}>
                  <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                </button>
              </div>
              <h4 className="text-lg font-black mt-2" style={{ fontFamily: "var(--hh-display)" }}>{it.title}</h4>
              <span className="nb-badge bg-white mt-1 inline-block">{it.category}</span>
              {it.description && <p className="text-sm mt-2">{it.description}</p>}
              <div className="text-xs font-bold mt-3 space-y-1">
                <p>Every {it.frequency_days} days</p>
                {it.last_done && <p>Last: {it.last_done.slice(0, 10)}</p>}
                {it.next_due && <p className={isDue(it) ? "text-black font-black" : ""}>Next due: {it.next_due.slice(0, 10)}</p>}
              </div>
              <button onClick={() => complete(it.id)} data-testid={`complete-maintenance-${it.id}`} className="nb-btn bg-[#B8CFA9] mt-3 px-3 py-1.5 text-xs">
                <CheckCircle2 strokeWidth={2.5} className="w-3.5 h-3.5 inline mr-1" /> Mark Done
              </button>
            </NBCard>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Maintenance Task</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="maintenance-title" required placeholder="e.g. Change HVAC filter" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <NBSelect testId="maintenance-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </NBSelect>
              <NBTextarea testId="maintenance-desc" placeholder="Notes" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div>
                <label className="text-xs font-black uppercase mb-1 block">Frequency (days)</label>
                <NBInput testId="maintenance-freq" type="number" min={1} value={form.frequency_days} onChange={(e) => setForm({ ...form, frequency_days: parseInt(e.target.value) || 30 })} />
              </div>
              <div>
                <label className="text-xs font-black uppercase mb-1 block">Next due (optional)</label>
                <NBInput type="date" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} />
              </div>
              <NBButton testId="submit-maintenance" type="submit" color="#E9B5A0" className="w-full">Save</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
